import React from 'react';
import classNames from 'classnames';
import { BringgIcon } from '@bringg/bringg-icons';
import { DSButtonSize } from '../shared';
import { DSButtonContent } from '../button-content';

import s from './index.module.scss';

// link and subtle link are not here and should be in a different component that also accept custom link component (for example react-router-dom Link)
export type DSButtonVariant = 'regular' | 'primary' | 'secondary' | 'danger' | 'warning';

// Because we inherit from the native button element, we don't need to add data-test-id or any other custom attributes
export interface DSButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: DSButtonVariant;

	size?: DSButtonSize;

	// If just want to display an icon button just pass the icon as children and set treatChildrenAsIcon to true if the children is not an BringgIcon icon element
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;

	children?: React.ReactNode;
	treatChildrenAsIcon?: boolean;
}

/**
 * Design System Button
 *
 * @see https://www.figma.com/design/x3dPTVfzcPhvz8sl04NwB4/Bringg?node-id=69-1755 Figma design
 */
export const DSButton = React.forwardRef<HTMLButtonElement, DSButtonProps>(
	//
	function DSButton(
		{
			size = 'medium',
			variant = 'regular',
			leftIcon,
			rightIcon,
			children,
			className,
			treatChildrenAsIcon,
			...nativeProps
		},
		ref
	) {
		const onlyIcon =
			(children && treatChildrenAsIcon) ||
			// Either no children and only have left or right - (instead you should set the icon as children and set treatChildrenAsIcon to true if the children is not an BringgIcon icon element)
			(!children && (leftIcon || rightIcon)) ||
			// If the children is bringg icon, and not specified to not treat as icon, we treat it as an icon
			(children &&
				treatChildrenAsIcon !== false &&
				!leftIcon &&
				!rightIcon &&
				React.isValidElement(children) &&
				children.type === BringgIcon);

		return (
			<button
				ref={ref}
				{...nativeProps}
				className={classNames(
					s.button,
					{
						// Size
						[s.small]: size === 'small',
						[s.medium]: size === 'medium',
						[s.large]: size === 'large',

						// Type
						[s.regular]: variant === 'regular',
						[s.primary]: variant === 'primary',
						[s.secondary]: variant === 'secondary',
						[s.danger]: variant === 'danger',
						[s.warning]: variant === 'warning',

						[s.onlyIcon]: onlyIcon
					},
					className
				)}
			>
				<DSButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
					{children}
				</DSButtonContent>
			</button>
		);
	}
);
